import React, { useEffect, useState } from 'react';
import { cloneDeep } from 'lodash';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { EnhancedButtonStatus } from '@components/_old/EnhancedButton';
import { FileType } from '@models/file';
import { getUserToken } from '@utils/userUtils';
import { resolveGraphqlBaseUrl } from '@utils/tenant-utils';
import { getError } from '@utils/graph-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { FormFieldType } from '@components/dynamic-form/types/inputTypes';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';

const drawerSections: IDynamicFormWidgetSection[] = [
  {
    inputs: {
      documentName: {
        name: 'documentName',
        title: 'Document Name',
        type: FormFieldType.text,
        placeholder: 'Enter document name',
        value: '',
        required: true,
      },
      document: {
        name: 'document',
        title: 'Document',
        type: FormFieldType.fileUploader,
        value: null,
        required: true,
        disabled: false,
        maxFileSizeInMB: 5,
        allowedFileTypes: [
          FileType.pdf,
          FileType.docx,
          FileType.jpeg,
          FileType.png,
        ],
      },
    },
  },
];

interface IAddDocumentDrawerProps {
  onClose: () => void;
  onSuccess: () => void;
  open: boolean;
  entityId: string;
}

const AddDocumentDrawer: React.FC<IAddDocumentDrawerProps> = ({
  open,
  onSuccess,
  onClose,
  entityId,
}) => {
  const [submitButtonDisabled] = useState<boolean>(false);

  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [inputsForm, setInputsForm] =
    useState<IDynamicFormWidgetSection[]>(drawerSections);

  useEffect(() => {
    initialize();
  }, []);

  const initialize = () => {
    try {
      const updatedInputs = cloneDeep(inputsForm);
      setInputsForm(updatedInputs);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const simulateFileUpload = async (
    documentName: string,
    file: File
  ): Promise<void> => {
    const allowedExtensions = ['pdf', 'docx', 'jpeg', 'jpg', 'png'];

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    setSubmitButtonState('loading');
    setFormDisabled(true);
    try {
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        toast.error(
          <ToastErrorMessage>
            {
              'Invalid file format. Please upload a PDF or DOCX or JPEG or PNG file.'
            }
          </ToastErrorMessage>
        );
        throw new Error(
          'Invalid file format. Please upload a PDF or DOCX or JPEG or PNG file.'
        );
      }

      const formData = new FormData();

      formData.append('formFile', file, file.name);
      formData.append(
        'operations',
        JSON.stringify({
          query: `
          mutation ($uploadedDocument:Upload!){
            production{
              entities{
                policy{
                  production{
                    uploadAdditionalDocument(entityId: "${entityId}"
                    documentName: "${documentName}"
                    uploadedDocument: $uploadedDocument) {
                      id
                    }
                  }
                }
              }
            }
          }
        `,
          variables: {
            uploadedDocument: null as string,
          },
        })
      );
      formData.append(
        'map',
        JSON.stringify({ formFile: ['variables.uploadedDocument'] })
      );

      const options = {
        method: 'POST',
        headers: {
          authorization: `Bearer ${getUserToken()}` || null,
          'GraphQL-preflight': '1',
        },
        body: formData,
      };

      try {
        const response = await fetch(
          `${resolveGraphqlBaseUrl()}/graphql`,
          options
        );

        if (response.ok) {
          toast.success(
            <ToastSuccessMessage>
              {'Document successfully uploaded'}
            </ToastSuccessMessage>
          );

          setTimeout(() => {
            setSubmitButtonState('success');
            onSuccess();
            onClose();
          }, 500);
        } else {
          setSubmitButtonState(undefined);
          toast.error(
            <ToastErrorMessage>{getError(response)}</ToastErrorMessage>
          );
        }
      } catch (error) {
        setSubmitButtonState(undefined);
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      }
    } catch (error) {
      toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
      throw error;
    } finally {
      setFormDisabled(false);
    }
  };

  const submitForm = async (values: Record<string, any>) => {
    const [_data] = normalizeDynamicFormDrawerValues(drawerSections, values);
    setFormDisabled(true);
    setSubmitButtonState('loading');
    try {
      await simulateFileUpload(values.documentName, values.document);
    } catch {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      title={'Add Document'}
      onClose={() => onClose()}
      isOpen={open}
      loading={!open}
      sections={inputsForm}
      onSubmit={(v) => submitForm(v)}
      submitButtonState={submitButtonState}
      isSubmitButtonDisabled={submitButtonDisabled}
      disableForm={formDisabled}
    />
  );
};

export default AddDocumentDrawer;
